import React from "react";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";

function SkeletonBlock({ className = "", ...props }) {
  return (
    <span
      className={`vg-chat-loading-shimmer block rounded-full bg-white/16 ${className}`}
      aria-hidden="true"
      {...props}
    />
  );
}

const EventDetailsLoadingSkeleton = ({ desktop = false }) => (
  <div
    className={`relative h-full min-h-0 w-full overflow-hidden text-white ${
      desktop
        ? "mx-auto max-w-[760px] rounded-[30px] bg-[#050812]"
        : "min-h-[100dvh] bg-[#050812]"
    }`}
    style={AUTH_GRADIENT_STYLE}
    role="status"
    aria-live="polite"
    aria-label="Loading event"
  >
    <div
      className="absolute inset-x-0 top-0 h-[26rem] bg-gradient-to-b from-white/[0.11] via-blue-400/[0.08] to-transparent"
      aria-hidden="true"
    />

    <div className="relative z-10 px-[30px] pb-[calc(var(--bottom-nav-clearance,112px)+48px)] pt-[calc(env(safe-area-inset-top,0px)+16px)]">
      <div className="flex items-center justify-between">
        <SkeletonBlock className="h-[44px] w-[44px] rounded-full bg-white/14" />
        <div className="flex gap-2">
          <SkeletonBlock className="h-[44px] w-[44px] rounded-full bg-white/14" />
          <SkeletonBlock className="h-[44px] w-[44px] rounded-full bg-white/14" />
        </div>
      </div>

      <div className="mt-[51px] rounded-[24px] border border-white/10 bg-black/15 p-4 shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
        <div className="flex items-center gap-4">
          <SkeletonBlock className="h-[86px] w-[86px] shrink-0 rounded-[20px] bg-white/18" />
          <div className="min-w-0 flex-1">
            <SkeletonBlock className="h-[25px] w-[78%] bg-white/24" />
            <SkeletonBlock className="mt-3 h-[17px] w-[58%] bg-white/16" />
          </div>
          <SkeletonBlock className="h-[34px] w-[34px] shrink-0 rounded-full bg-white/14" />
        </div>
      </div>

      <div className="mt-4 flex gap-2 overflow-hidden">
        <SkeletonBlock className="h-[32px] w-[94px] shrink-0 bg-white/18" />
        <SkeletonBlock className="h-[32px] w-[76px] shrink-0 bg-white/14" />
        <SkeletonBlock className="h-[32px] w-[106px] shrink-0 bg-white/14" />
      </div>

      <div className="mt-8">
        <SkeletonBlock className="h-[32px] w-[56%] bg-white/22" />
        <SkeletonBlock className="mt-4 h-[16px] w-full bg-white/14" />
        <SkeletonBlock className="mt-3 h-[16px] w-[91%] bg-white/14" />
        <SkeletonBlock className="mt-3 h-[16px] w-[68%] bg-white/14" />
      </div>

      <div className="mt-8 space-y-4">
        {["w-[72%]", "w-[86%]", "w-[64%]"].map((widthClass, index) => (
          <div
            key={`event-details-loading-row-${index}`}
            className="flex items-center gap-3"
          >
            <SkeletonBlock className="h-[28px] w-[28px] shrink-0 rounded-full bg-white/14" />
            <div className="min-w-0 flex-1">
              <SkeletonBlock className={`h-[15px] bg-white/18 ${widthClass}`} />
              <SkeletonBlock className="mt-2 h-[12px] w-[44%] bg-white/10" />
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-9 flex items-start gap-[10px] overflow-hidden"
        data-testid="event-details-loading-participants"
      >
        {[0, 1, 2, 3].map((index) => (
          <SkeletonBlock
            key={`event-details-loading-person-${index}`}
            className="h-[55px] w-[55px] shrink-0 rounded-full border-2 border-[#111a36] bg-white/16"
            data-testid="event-details-loading-participant"
          />
        ))}
        <SkeletonBlock className="ml-[6px] h-[18px] w-[92px] shrink-0 self-center bg-white/14" />
      </div>

      <SkeletonBlock className="mt-9 h-[55px] w-full bg-white/22" />
    </div>

    <span className="sr-only">Loading event</span>
  </div>
);

export default EventDetailsLoadingSkeleton;
